import { useState } from 'react';
import { useSearchParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, TextField, Button, Stack, Alert } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { api } from '../api/client.js';
import { useLang } from '../i18n/lang.js';
import { AUTH_COPY } from '../i18n/authCopy.js';
import { LangToggle } from '../i18n/LangToggle.js';

/**
 * Public target of the reset link. Reads `?token=` and sets a new password.
 * `?welcome=1` marks an admin-provisioned account setting its first password —
 * same mechanics, but the copy reads as onboarding rather than recovery.
 */
export function ResetPasswordPage() {
  const [lang] = useLang();
  const c = AUTH_COPY[lang].reset;
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';
  const welcome = params.get('welcome') === '1';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) return setError(c.tooShort);
    if (password !== confirm) return setError(c.mismatch);
    setBusy(true);
    try {
      await api.post('/v1/auth/reset-password', { token, password });
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch {
      setError(welcome ? c.badInvite : c.badToken);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 2, bgcolor: 'background.default' }}>
      <Paper sx={{ p: 5, maxWidth: 440, width: '100%' }}>
        <LangToggle sx={{ float: 'right', mt: -2, mr: -2 }} />
        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 700, mb: 3, textAlign: 'center' }}>
          ReplayOps
        </Typography>

        {done ? (
          <Stack spacing={2} alignItems="center" textAlign="center">
            <CheckCircleIcon sx={{ fontSize: 56, color: 'success.main' }} />
            <Typography variant="h6">{welcome ? c.welcomeDoneTitle : c.doneTitle}</Typography>
            <Typography color="text.secondary">
              {welcome ? c.welcomeDoneBody : c.doneBody}
            </Typography>
            <Button component={RouterLink} to="/login" variant="contained">
              {c.goToSignIn}
            </Button>
          </Stack>
        ) : !token ? (
          <Stack spacing={2} alignItems="center" textAlign="center">
            <Typography variant="h6">{c.noTokenTitle}</Typography>
            <Typography color="text.secondary">{c.noTokenBody}</Typography>
            {welcome ? (
              <Typography variant="body2" color="text.secondary">
                {c.noTokenInvite}
              </Typography>
            ) : (
              <Button component={RouterLink} to="/forgot-password" variant="outlined">
                {c.requestNew}
              </Button>
            )}
          </Stack>
        ) : (
          <form onSubmit={submit}>
            <Typography variant="h6" sx={{ mb: welcome ? 1 : 2 }}>
              {welcome ? c.welcomeTitle : c.title}
            </Typography>
            {welcome && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {c.welcomeBody}
              </Typography>
            )}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <TextField
              label={welcome ? c.password : c.newPassword}
              type="password"
              fullWidth
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              helperText={c.helper}
            />
            <TextField
              label={c.confirm}
              type="password"
              fullWidth
              margin="normal"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
            <Button type="submit" variant="contained" fullWidth sx={{ mt: 2 }} disabled={busy}>
              {welcome ? c.welcomeSubmit : c.submit}
            </Button>
          </form>
        )}
      </Paper>
    </Box>
  );
}
